import { useState, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '@/contexts/SharedAuthContext';
import { trpc } from '@/lib/trpc';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Checkbox } from '@/components/ui/checkbox';
import { Label } from '@/components/ui/label';
import { Camera, Shield, Pencil, Search, ChevronLeft, ChevronRight, ChevronDown, Type, Lock } from 'lucide-react';
import { Link } from 'react-router-dom';
import { Input } from '@/components/ui/input';
import { getAreaBadgeStyle } from '@/lib/brand-colors';
import type {} from '../../../shared/types';
import { useAccessibilitySettings, type FontSize } from '@/hooks/useAccessibilitySettings';

type TFunction = ReturnType<typeof useTranslation>['t'];

const ALL_ASSIGNABLE_ROLES = [
  'admin', 'tech', 'finance', 'people', 'clapper', 'growth',
  'branding', 'business', 'creative', 'data', 'executive',
  'ownedmedia', 'paidmedia', 'partnerships',
] as const;
type AssignableRole = (typeof ALL_ASSIGNABLE_ROLES)[number];

type TabPermission = 'tab:inventory' | 'tab:approvals' | 'tab:users' | 'tab:performance' | 'tab:ia-native';

interface SubTabConfig { id: string; label: string }
interface PlatformTabConfig {
  id: TabPermission;
  label: string;
  description: string;
  grantedByRoles: AssignableRole[];
  subTabs: SubTabConfig[];
}

// Función de `t`: labels/descriptions salen de i18n y `t` no existe a nivel de módulo.
const PLATFORM_TABS = (t: TFunction): PlatformTabConfig[] => [
  {
    id: 'tab:inventory',
    label: t('profile.tabs.dispositivos'),
    description: t('profile.tabs.inventario_aprobaciones_info_util'),
    grantedByRoles: ['tech', 'finance', 'admin'],
    subTabs: [
      { id: 'subtab:inventory:main', label: t('profile.tabs.inventario') },
      { id: 'subtab:inventory:approval', label: t('profile.tabs.aprobaciones_de_compra') },
      { id: 'subtab:inventory:useful-info', label: t('profile.tabs.info_util') },
    ],
  },
  {
    id: 'tab:users',
    label: t('profile.tabs.gestion_de_usuarios'),
    description: t('profile.tabs.clappi_in_out_altas_y_bajas'),
    grantedByRoles: ['tech', 'finance', 'people', 'admin'],
    subTabs: [
      { id: 'subtab:users:onboarding', label: t('profile.tabs.in_out_altas') },
      { id: 'subtab:users:offboarding', label: t('profile.tabs.in_out_bajas') },
      { id: 'subtab:users:inactive', label: t('profile.tabs.in_out_inactivos') },
      { id: 'subtab:users:community-map', label: t('profile.tabs.community_map') },
    ],
  },
  {
    id: 'tab:performance',
    label: t('profile.tabs.performance'),
    description: t('profile.tabs.evaluaciones_de_desempeno'),
    grantedByRoles: ['people', 'admin'],
    subTabs: [
      { id: 'subtab:performance:evaluations', label: t('profile.tabs.evaluaciones') },
    ],
  },
  {
    id: 'tab:ia-native',
    label: t('profile.tabs.ia_native'),
    description: t('profile.tabs.dashboard_clappers_informes_subscriptions_observability'),
    grantedByRoles: ['growth', 'finance', 'admin'],
    subTabs: [
      { id: 'subtab:ia-native:dashboard', label: t('profile.tabs.dashboard') },
      { id: 'subtab:ia-native:clappers', label: t('profile.tabs.clappers') },
      { id: 'subtab:ia-native:subscriptions', label: t('profile.tabs.ai_subscriptions') },
      { id: 'subtab:ia-native:observability', label: t('profile.tabs.ai_observability') },
    ],
  },
  {
    id: 'tab:approvals',
    label: t('profile.tabs.aprobaciones_de_compra'),
    description: t('profile.tabs.aprobar_rechazar_solicitudes_de_compra'),
    grantedByRoles: ['tech', 'finance', 'admin'],
    subTabs: [],
  },
];

function RoleBadge({ role }: { role: string }) {
  const style = getAreaBadgeStyle(role);
  return (
    <span
      className="inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium capitalize border"
      style={{
        backgroundColor: style.bg,
        color: style.text,
        borderColor: style.border,
      }}
    >
      {role}
    </span>
  );
}

interface EditRolesDialogProps {
  user: { userId: string; name: string; email: string; groups: string[] };
  open: boolean;
  onClose: () => void;
  onSaved: () => void;
}

function TabToggle({ on, onClick }: { on: boolean; onClick: () => void }) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={on}
      onClick={onClick}
      className={`relative inline-flex h-5 w-9 shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors focus:outline-hidden ${
        on ? 'bg-primary' : 'bg-input'
      }`}
    >
      <span
        className={`pointer-events-none inline-block h-4 w-4 rounded-full bg-white shadow-lg transform transition-transform ${
          on ? 'translate-x-4' : 'translate-x-0'
        }`}
      />
    </button>
  );
}

function EditRolesDialog({ user, open, onClose, onSaved }: EditRolesDialogProps) {
  const { t } = useTranslation();
  const [selected, setSelected] = useState<AssignableRole[]>(
    user.groups.filter((g): g is AssignableRole =>
      ALL_ASSIGNABLE_ROLES.includes(g as AssignableRole)
    )
  );
  // extraTabs = granted beyond role; deniedItems = blocked (main tabs or subtab IDs)
  const [extraTabs, setExtraTabs] = useState<TabPermission[]>([]);
  const [deniedItems, setDeniedItems] = useState<string[]>([]);
  const [expandedTabs, setExpandedTabs] = useState<TabPermission[]>([]);

  const { data: extraPermsData } = trpc.extraPermissions.getByEmail.useQuery(
    { email: user.email },
    { enabled: open }
  );

  useEffect(() => {
    if (extraPermsData) {
      setExtraTabs((extraPermsData.tabs ?? []) as TabPermission[]);
      setDeniedItems(extraPermsData.deniedTabs ?? []);
    }
  }, [extraPermsData]);

  const updateMutation = trpc.profile.updateUserRoles.useMutation();
  const setExtraPermsMutation = trpc.extraPermissions.set.useMutation();
  const isSaving = updateMutation.isPending || setExtraPermsMutation.isPending;

  const hasRoleAccess = (tabId: TabPermission) => {
    const tab = PLATFORM_TABS(t).find((entry) => entry.id === tabId);
    return tab?.grantedByRoles.some((r) => selected.includes(r)) ?? false;
  };

  const isTabOn = (tabId: TabPermission) =>
    (hasRoleAccess(tabId) || extraTabs.includes(tabId)) && !deniedItems.includes(tabId);

  const isSubTabOn = (tabId: TabPermission, subId: string) =>
    isTabOn(tabId) && !deniedItems.includes(subId);

  const tabSource = (tabId: TabPermission): 'rol' | 'extra' | null => {
    if (deniedItems.includes(tabId)) return null;
    if (hasRoleAccess(tabId)) return 'rol';
    if (extraTabs.includes(tabId)) return 'extra';
    return null;
  };

  const handleTabToggle = (tabId: TabPermission) => {
    const on = isTabOn(tabId);
    if (on) {
      if (hasRoleAccess(tabId)) {
        setDeniedItems((prev) => [...prev, tabId]);
      } else {
        setExtraTabs((prev) => prev.filter((id) => id !== tabId));
      }
    } else {
      if (deniedItems.includes(tabId)) {
        setDeniedItems((prev) => prev.filter((id) => id !== tabId));
      } else {
        setExtraTabs((prev) => [...prev, tabId]);
      }
    }
  };

  const handleSubTabToggle = (tabId: TabPermission, subId: string) => {
    const on = isSubTabOn(tabId, subId);
    if (on) {
      setDeniedItems((prev) => [...prev, subId]);
    } else {
      setDeniedItems((prev) => prev.filter((id) => id !== subId));
    }
  };

  const toggleExpanded = (tabId: TabPermission) => {
    setExpandedTabs((prev) =>
      prev.includes(tabId) ? prev.filter((id) => id !== tabId) : [...prev, tabId]
    );
  };

  const handleSave = async () => {
    try {
      await Promise.all([
        updateMutation.mutateAsync({ username: user.userId, groups: selected }),
        setExtraPermsMutation.mutateAsync({ email: user.email, tabs: extraTabs, deniedTabs: deniedItems }),
      ]);
      toast.success(t('profile.roles_updated', { name: user.name }));
      onSaved();
      onClose();
    } catch (err: any) {
      toast.error(t('profile.roles_update_error', { message: err.message }));
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-md max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{t('profile.edit_roles_title_for', { name: user.name })}</DialogTitle>
        </DialogHeader>
        <p className="text-sm text-muted-foreground">{user.email}</p>

        {/* Roles */}
        <div className="space-y-1 pt-1">
          <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Roles</p>
          <div className="grid grid-cols-2 gap-1">
            {ALL_ASSIGNABLE_ROLES.map((role) => (
              <div key={role} className="flex items-center gap-2 py-1">
                <Checkbox
                  id={`role-${role}`}
                  checked={selected.includes(role)}
                  onCheckedChange={() =>
                    setSelected((prev) =>
                      prev.includes(role) ? prev.filter((r) => r !== role) : [...prev, role]
                    )
                  }
                />
                <Label htmlFor={`role-${role}`} className="capitalize cursor-pointer">
                  <RoleBadge role={role} />
                </Label>
              </div>
            ))}
          </div>
        </div>

        {/* Tabs */}
        <div className="space-y-2 pt-2 border-t">
          <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">{t('profile.tabs.acceso_a_tabs')}</p>
          <div className="space-y-1">
            {PLATFORM_TABS(t).map((tab) => {
              const on = isTabOn(tab.id);
              const source = tabSource(tab.id);
              const isExpanded = expandedTabs.includes(tab.id);
              const hasSubTabs = tab.subTabs.length > 0;

              return (
                <div key={tab.id} className="rounded-lg border overflow-hidden">
                  {/* Main tab row */}
                  <div className={`flex items-center gap-2 px-3 py-2 ${on ? 'bg-primary/5' : 'bg-muted/30'}`}>
                    {/* Expand button (only if has sub-tabs and is ON) */}
                    {hasSubTabs ? (
                      <button
                        type="button"
                        onClick={() => on && toggleExpanded(tab.id)}
                        disabled={!on}
                        className="text-muted-foreground hover:text-foreground disabled:opacity-30"
                      >
                        <ChevronDown
                          className={`h-4 w-4 transition-transform ${isExpanded && on ? 'rotate-0' : '-rotate-90'}`}
                        />
                      </button>
                    ) : (
                      <span className="w-4" />
                    )}

                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-1.5">
                        <span className={`text-sm font-medium ${!on ? 'text-muted-foreground' : ''}`}>
                          {tab.label}
                        </span>
                        {source === 'rol' && (
                          <span className="text-xs rounded-full bg-[#EBF7FF] text-[#0C4A6E] px-1.5 py-0.5">rol</span>
                        )}
                        {source === 'extra' && (
                          <span className="text-xs rounded-full bg-[#E5F9F7] text-[#0D9488] px-1.5 py-0.5">extra</span>
                        )}
                        {!on && deniedItems.includes(tab.id) && (
                          <span className="text-xs rounded-full bg-[#FEF1EC] text-[#7C2D12] px-1.5 py-0.5">bloqueado</span>
                        )}
                      </div>
                    </div>

                    <TabToggle on={on} onClick={() => handleTabToggle(tab.id)} />
                  </div>

                  {/* Sub-tabs */}
                  {hasSubTabs && isExpanded && on && (
                    <div className="border-t bg-background divide-y">
                      {tab.subTabs.map((sub) => {
                        const subOn = isSubTabOn(tab.id, sub.id);
                        return (
                          <div key={sub.id} className="flex items-center justify-between px-4 py-2">
                            <span className={`text-sm ${!subOn ? 'text-muted-foreground' : ''}`}>
                              {sub.label}
                            </span>
                            <TabToggle
                              on={subOn}
                              onClick={() => handleSubTabToggle(tab.id, sub.id)}
                            />
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>

        <div className="flex flex-wrap justify-end gap-2 mt-2">
          <Button variant="outline" onClick={onClose} disabled={isSaving}>
            {t('profile.cancel')}
          </Button>
          <Button onClick={handleSave} disabled={isSaving}>
            {isSaving ? t('profile.saving') : t('profile.save')}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

export function ProfilePage() {
  const { t } = useTranslation();
  const { user } = useAuth();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [avatarObjectUrl, setAvatarObjectUrl] = useState<string | null>(null);
  const [isUploading, setIsUploading] = useState(false);
  const [editingUser, setEditingUser] = useState<{
    userId: string;
    name: string;
    email: string;
    groups: string[];
  } | null>(null);

  // Search + pagination state
  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState('');
  const [tokenHistory, setTokenHistory] = useState<(string | undefined)[]>([undefined]);
  const [pageIndex, setPageIndex] = useState(0);
  const currentToken = tokenHistory[pageIndex];

  // Debounce search input
  useEffect(() => {
    const timer = setTimeout(() => {
      setSearch(searchInput);
      setTokenHistory([undefined]);
      setPageIndex(0);
    }, 300);
    return () => clearTimeout(timer);
  }, [searchInput]);

  const { data: profile, refetch: refetchProfile } = trpc.profile.getProfile.useQuery();
  const { data: avatarData, refetch: refetchAvatar } = trpc.profile.getAvatarUrl.useQuery(
    undefined,
    { enabled: !!profile?.hasAvatar, retry: false, staleTime: 50 * 60 * 1000 }
  );
  const getUploadUrlMutation = trpc.profile.getAvatarUploadUrl.useMutation();
  const confirmUploadMutation = trpc.profile.confirmAvatarUpload.useMutation();

  const isAdmin = user?.roles?.includes('admin') ?? false;

  const {
    data: usersData,
    isLoading: usersLoading,
    refetch: refetchUsers,
  } = trpc.profile.listAllUsers.useQuery(
    { search: search || undefined, paginationToken: currentToken },
    { enabled: isAdmin }
  );

  const allUsers = usersData?.users ?? [];
  const nextToken = usersData?.nextToken;

  function goToNextPage() {
    if (!nextToken) return;
    setTokenHistory((prev) => [...prev.slice(0, pageIndex + 1), nextToken]);
    setPageIndex((prev) => prev + 1);
  }

  function goToPrevPage() {
    if (pageIndex === 0) return;
    setPageIndex((prev) => prev - 1);
  }

  const avatarSrc = avatarObjectUrl ?? (profile?.hasAvatar ? avatarData?.url : null);
  const initials = user?.name
    ? user.name
        .split(' ')
        .map((n) => n[0])
        .slice(0, 2)
        .join('')
        .toUpperCase()
    : user?.email?.[0]?.toUpperCase() ?? '?';

  async function handleAvatarChange(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    if (!file) return;

    // Preview immediately
    const objectUrl = URL.createObjectURL(file);
    setAvatarObjectUrl(objectUrl);
    setIsUploading(true);

    try {
      const contentType = file.type || 'image/jpeg';
      const { url } = await getUploadUrlMutation.mutateAsync({ contentType });
      await fetch(url, {
        method: 'PUT',
        body: file,
        headers: { 'Content-Type': contentType },
      });
      await confirmUploadMutation.mutateAsync();
      await refetchProfile();
      await refetchAvatar();
      toast.success(t('profile.photo_updated'));
    } catch {
      toast.error(t('profile.photo_upload_error'));
      setAvatarObjectUrl(null);
    } finally {
      setIsUploading(false);
    }
  }

  const { fontSize, setFontSize } = useAccessibilitySettings();

  if (!user) return null;

  const fontSizeOptions: { value: FontSize; label: string; px: string }[] = [
    { value: 'sm', label: 'A', px: '14px' },
    { value: 'md', label: 'A', px: '16px' },
    { value: 'lg', label: 'A', px: '18px' },
    { value: 'xl', label: 'A', px: '20px' },
  ];

  return (
    <div className="space-y-6 max-w-3xl mx-auto">
      <h2 className="text-3xl font-bold tracking-tight">{t('profile.title')}</h2>

      {/* Profile Card */}
      <Card>
        <CardContent className="pt-6">
          <div className="flex items-center gap-6">
            {/* Avatar */}
            <div className="relative shrink-0">
              <div className="h-20 w-20 rounded-full overflow-hidden bg-brand-gray1 flex items-center justify-center">
                {avatarSrc ? (
                  <img src={avatarSrc} alt="Avatar" className="h-full w-full object-cover" />
                ) : (
                  <span className="text-white text-2xl font-bold">{initials}</span>
                )}
              </div>
              <button
                onClick={() => fileInputRef.current?.click()}
                disabled={isUploading}
                className="absolute bottom-0 right-0 flex h-7 w-7 items-center justify-center rounded-full bg-white border border-gray-200 shadow-xs hover:bg-gray-50 disabled:opacity-50"
                title={t('profile.change_photo')}
              >
                <Camera className="h-3.5 w-3.5 text-gray-600" />
              </button>
              <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                className="hidden"
                onChange={handleAvatarChange}
              />
            </div>

            {/* Info */}
            <div className="flex-1 min-w-0">
              <p className="text-xl font-semibold truncate">{user.name}</p>
              <p className="text-sm text-muted-foreground truncate">{user.email}</p>
              <div className="flex flex-wrap gap-1.5 mt-2">
                {user.roles?.map((role) => <RoleBadge key={role} role={role} />) ?? (
                  <RoleBadge role={user.role} />
                )}
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Admin: Role Management */}
      {isAdmin && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Shield className="h-5 w-5" />
              {t('profile.role_management')}
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            {/* Search */}
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder={t('profile.search_placeholder')}
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
                className="pl-9"
              />
            </div>

            {/* User list */}
            {usersLoading ? (
              <p className="text-sm text-muted-foreground">{t('profile.loading_users')}</p>
            ) : allUsers.length === 0 ? (
              <p className="text-sm text-muted-foreground text-center py-4">
                {search ? t('profile.no_users_found') : t('profile.no_users')}
              </p>
            ) : (
              <div className="space-y-2">
                {allUsers.map((u) => (
                  <div
                    key={u.userId}
                    className="flex items-center justify-between rounded-lg border px-4 py-3 hover:bg-muted/50 transition-colors"
                  >
                    <div className="flex-1 min-w-0 mr-4">
                      <p className="text-sm font-medium truncate">{u.name}</p>
                      <p className="text-xs text-muted-foreground truncate">{u.email}</p>
                    </div>
                    <div className="flex items-center gap-2 shrink-0">
                      <div className="flex flex-wrap gap-1">
                        {u.groups.length > 0 ? (
                          u.groups.map((g) => <RoleBadge key={g} role={g} />)
                        ) : (
                          <span className="text-xs text-muted-foreground">{t('profile.no_roles')}</span>
                        )}
                      </div>
                      <Button
                        variant="ghost"
                        size="sm"
                        className="h-7 w-7 p-0"
                        onClick={() => setEditingUser(u)}
                        title={t('profile.edit_roles_title')}
                      >
                        <Pencil className="h-3.5 w-3.5" />
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            )}

            {/* Pagination */}
            {(pageIndex > 0 || nextToken) && (
              <div className="flex items-center justify-between pt-2 border-t">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={goToPrevPage}
                  disabled={pageIndex === 0}
                  className="gap-1"
                >
                  <ChevronLeft className="h-4 w-4" />
                  {t('profile.previous')}
                </Button>
                <span className="text-xs text-muted-foreground">{t('profile.page', { page: pageIndex + 1 })}</span>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={goToNextPage}
                  disabled={!nextToken}
                  className="gap-1"
                >
                  {t('profile.next')}
                  <ChevronRight className="h-4 w-4" />
                </Button>
              </div>
            )}
            <div className="pt-2 border-t">
              <Link
                to="/admin/users/permissions"
                className="flex items-center gap-2 text-sm font-medium text-brand-green hover:underline"
              >
                <Lock className="h-4 w-4" />
                {t('profile.tabs.gestionar_permisos_adicionales')}
              </Link>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Accessibility settings */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-base">
            <Type className="h-4 w-4" />
            {t('profile.accessibility.title')}
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div>
            <p className="text-sm text-muted-foreground mb-3">{t('profile.accessibility.font_size_label')}</p>
            <div className="flex items-center gap-2">
              {fontSizeOptions.map((opt) => (
                <button
                  key={opt.value}
                  onClick={() => setFontSize(opt.value)}
                  title={opt.px}
                  className={`flex items-center justify-center rounded-md border transition-colors px-3 py-2 font-medium ${
                    fontSize === opt.value
                      ? 'border-brand-green bg-brand-green/10 text-brand-green'
                      : 'border-brand-gray4 text-brand-gray2 hover:border-brand-green/50 hover:bg-brand-offwhite'
                  }`}
                  style={{ fontSize: opt.px, lineHeight: 1 }}
                >
                  {opt.label}
                </button>
              ))}
              <span className="ml-2 text-xs text-muted-foreground">
                {fontSize === 'sm' && t('profile.accessibility.font_sm')}
                {fontSize === 'md' && t('profile.accessibility.font_md')}
                {fontSize === 'lg' && t('profile.accessibility.font_lg')}
                {fontSize === 'xl' && t('profile.accessibility.font_xl')}
              </span>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Edit Roles Dialog */}
      {editingUser && (
        <EditRolesDialog
          user={editingUser}
          open={!!editingUser}
          onClose={() => setEditingUser(null)}
          onSaved={() => refetchUsers()}
        />
      )}
    </div>
  );
}
